import { useEffect, useMemo, useRef, useState } from 'react';
import {
  useIntakeBatchQuery, useBulkIntakeActionMutation, useCancelIntakeBatchMutation, useCrmContactTypesQuery,
  useResumeIntakeBatchMutation, useCrmTagsQuery,
  type CrmTagVocabulary, type IntakeItem, type IntakeProgress,
} from './api.js';
import { EMPTY_VOCABULARY } from './CrmTagPicker.js';
import { INTAKE_KINDS, labelFor, toSelection } from './crmTagSelection.js';
import { CAPTURED_FROM, ItemDetail } from './ItemDetail.js';
import { DrawerActions, DrawerFooter, DrawerSource } from './ReviewDrawer.js';
import { Alert, Button, Card, EmptyState, Spinner, formatWhen } from './ui.js';

/**
 * One import, row by row.
 *
 * The words are the customer's, not the engine's: "New contact", "Update existing
 * contact", "Possible duplicate" rather than crm_operation, resolver tier or policy
 * branch. Somebody deciding whether to approve two hundred contacts should not have to
 * learn our vocabulary first.
 */

const STATUS_LABEL: Record<string, string> = {
  pending: 'Waiting',
  processing: 'Checking',
  cancelled: 'Stopped',
  extracting: 'Reading',
  needs_review: 'Needs review',
  ready: 'Ready',
  duplicate: 'Possible duplicate',
  invalid: 'Not usable',
  executing: 'Working',
  /*
   * Approved, and nothing is running.
   *
   * `executing` reads as "Working", which was the only thing this row could say while
   * held did not exist — so an item that had been stranded before it ever reached the CRM
   * showed as busy indefinitely. "Working" and "not working" are the two states a person
   * checking on an import is actually trying to tell apart, and the state model now
   * distinguishes them, so the label follows the state rather than guessing at it.
   */
  held: 'Sync paused',
  completed: 'Done',
  failed: 'Failed',
  skipped: 'No action',
};

const STATUS_TONE: Record<string, string> = {
  ready: 'bg-emerald-50 text-emerald-800',
  needs_review: 'bg-amber-50 text-amber-800',
  duplicate: 'bg-amber-50 text-amber-800',
  invalid: 'bg-red-50 text-red-800',
  failed: 'bg-red-50 text-red-800',
  skipped: 'bg-slate-100 text-slate-600',
  cancelled: 'bg-slate-100 text-slate-600',
  processing: 'bg-sky-50 text-sky-800',
  // Neutral, not amber: paused is a workspace setting, not something to act on.
  held: 'bg-slate-100 text-slate-600',
};

const ERROR_LABEL: Record<string, string> = {
  insufficient_contact_data: 'Not enough to identify anybody',
  duplicate_row: 'Already in this file',
  invalid_email: 'Email could not be read',
  invalid_phone: 'Phone could not be read',
  cell_too_large: 'A value was too long',
  malformed_row: 'This row could not be read',
  transient_error: 'Could not reach your CRM — try again',
  provider_unavailable: 'Your CRM did not answer after several tries',
  no_candidate: 'Nothing on this row to work with',
  throttled: 'Your CRM asked us to slow down — it will be tried again',
  // A failed row carries the class of what went wrong.
  auth: 'Your CRM refused the connection — reconnect it in Settings',
  validation: 'Your CRM would not accept this contact',
  not_found: 'Your CRM could not find what this row refers to',
  conflict: 'Your CRM already had a conflicting record',
  rate_limit: 'Your CRM asked us to slow down — it will be tried again',
  transient: 'Could not reach your CRM — try again',
  poison: 'This row could not be processed',
  internal: 'Something went wrong with this row',
};

/**
 * A code the screen has no words for is still not shown as a code: a person reading
 * "permission_denied" learns nothing they can act on, and learns that the screen was
 * not written for them.
 */
const statusLabel = (s: string) => STATUS_LABEL[s] ?? 'Other';
const errorLabel = (c: string) => ERROR_LABEL[c] ?? 'Something went wrong with this row';

/** A row's CRM tags, by name. Empty when it has none. */
function tagsOf(item: IntakeItem, vocab: CrmTagVocabulary): string {
  if (vocab.pending) return '';
  return toSelection(
    { commercialStage: item.relationship, relationshipTypeKeys: [], crmTagKeys: item.tagKeys },
    vocab, INTAKE_KINDS,
  ).map((id) => labelFor(id, vocab)).join(', ');
}

/** What would happen to this row, said the way a person would say it. */
function proposedAction(item: IntakeItem): string {
  if (item.status === 'invalid') return '—';
  if (item.status === 'duplicate') return 'No action — already in this file';
  if (item.status === 'skipped') return 'No action';
  return item.identityResolved ? 'Update existing contact' : 'New contact';
}

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'ready', label: 'Ready' },
  { id: 'needs_review', label: 'Needs review' },
  { id: 'duplicate', label: 'Duplicates' },
  { id: 'invalid', label: 'Not usable' },
  { id: 'skipped', label: 'No action' },
  { id: 'failed', label: 'Failed' },
  { id: 'cancelled', label: 'Stopped' },
] as const;

/**
 * How far along, in rows.
 *
 * A count, not a bar with a percentage invented from it — well, a bar too, but one whose
 * width is exactly resolved over total and whose label is the two numbers. The number is
 * the honest part; the bar is only there because a proportion is easier to read at a
 * glance than two four-digit figures.
 */
function ProgressPanel({ progress }: { progress: IntakeProgress }) {
  const pct = progress.total > 0
    ? Math.round((progress.resolved / progress.total) * 100) : 0;
  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-sm font-medium text-slate-900">
          {progress.cancelRequested ? 'Stopping…' : 'Checking contacts'}
        </p>
        <p className="text-sm tabular-nums text-slate-600">
          {progress.resolved.toLocaleString()} / {progress.total.toLocaleString()}
        </p>
      </div>
      <div
        className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-100"
        role="progressbar"
        aria-valuenow={progress.resolved}
        aria-valuemin={0}
        aria-valuemax={progress.total}
        aria-label="Contacts checked"
      >
        <div className="h-full rounded-full bg-slate-900 transition-all" style={{ width: `${pct}%` }} />
      </div>
      <p className="mt-2 text-xs text-slate-500">
        {progress.queued.toLocaleString()} waiting · {progress.processing.toLocaleString()} in progress.
        You can close this page; it keeps going.
      </p>
    </Card>
  );
}

/** What a bulk action did to each contact, in plain words. */
const BULK_OUTCOME: Record<string, string> = {
  updated: 'updated',
  not_eligible: 'left as they are (already in your CRM or already decided)',
  unknown_contact_type: 'not changed (that Contact Type is not available)',
  contact_type_required: 'still need a Contact Type',
  contact_type_unsupported: 'need a Contact Type your CRM offers',
  approve: 'approved',
  stale_plan: 'changed since you looked; check them again',
};

export function BatchDetail({ batchId, onBack, backLabel = 'Back to Capture', showBack = true }: {
  batchId: string; onBack: () => void;
  /** Where Back goes, in words: Capture after an upload. */
  backLabel?: string;
  /** False inside the Review drawer, which brings its own Back and Close. */
  showBack?: boolean;
}) {
  /*
   * Polled while there is anything to watch.
   *
   * Deliberately not a socket. The state lives in the database, so a poll reads the
   * truth rather than a stream of events that has to be reassembled into it — and, more
   * to the point, closing the browser must not affect the import. A live connection that
   * mattered would make that false.
   */
  const { data, isLoading, error, refetch } = useIntakeBatchQuery(batchId, {
    pollingInterval: 3_000,
  });
  const [bulk, { isLoading: acting }] = useBulkIntakeActionMutation();
  // The CRM's own contact types (not tags): a separate list, from the connected CRM.
  const { data: typeList } = useCrmContactTypesQuery();
  const [cancelBatch, { isLoading: cancelling }] = useCancelIntakeBatchMutation();
  const [resumeBatch, { isLoading: resuming }] = useResumeIntakeBatchMutation();
  const [filter, setFilter] = useState<string>('all');
  const [query, setQuery] = useState('');
  const [openItem, setOpenItem] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [result, setResult] = useState<string | null>(null);
  const vocab = useCrmTagsQuery().data ?? EMPTY_VOCABULARY;
  /** Each contact's open button, by id — one per layout, only one of them visible. */
  const openRefs = useRef(new Map<string, HTMLButtonElement[]>());
  /** After a decision on one contact: where focus goes once the list is back. */
  const [focusAfter, setFocusAfter] = useState<string[] | null>(null);
  const register = (id: string) => (el: HTMLButtonElement | null) => {
    const list = (openRefs.current.get(id) ?? []).filter((x) => x.isConnected && x !== el);
    if (el) list.push(el);
    openRefs.current.set(id, list);
  };
  useEffect(() => {
    if (!focusAfter || openItem) return;
    const order = focusAfter;
    setFocusAfter(null);
    requestAnimationFrame(() => {
      for (const id of order) {
        const el = (openRefs.current.get(id) ?? []).find((x) => x.isConnected && x.offsetParent !== null);
        if (el) { el.focus(); return; }
      }
    });
  }, [focusAfter, openItem]);

  const items = useMemo(() => {
    const all = data?.items ?? [];
    const byStatus = filter === 'all' ? all : all.filter((i) => i.status === filter);
    const q = query.trim().toLowerCase();
    if (!q) return byStatus;
    // Filtered in the browser over the page already fetched, so a contact's name never
    // travels in a URL or a query string.
    return byStatus.filter((i) => {
      const c = i.candidate;
      return [c?.fullName, c?.company, c?.emails[0]?.normalized]
        .some((v) => (v ?? '').toLowerCase().includes(q));
    });
  }, [data, filter, query]);

  const eligible = items.filter((i) => !i.approvalState && i.planHash && (i.status === 'needs_review' || i.status === 'ready'));

  if (openItem) {
    const back = (order: string[]) => { setOpenItem(null); setFocusAfter(order); void refetch(); };
    const ids = items.map((i) => i.id);
    const at = ids.indexOf(openItem);
    return (
      <ItemDetail
        itemId={openItem}
        onBack={() => back([openItem])}
        onDecided={(message) => {
          setResult(message);
          // The next contact in the list, or the one before it at the end.
          back(at < 0 ? [] : [...ids.slice(at + 1), ...ids.slice(0, at).reverse()]);
        }}
      />
    );
  }
  if (isLoading) return <Spinner label="Loading import…" />;
  if (error || !data) return <Alert tone="error">That import could not be loaded.</Alert>;

  const n = (k: string) => data.tally.byStatus[k] ?? 0;
  const progress = data.progress;
  // Resumable rather than "finished": rows that were stopped or that the CRM never
  // answered for are still real rows, and picking them up is a different action from
  // starting over. Rows imported before a CRM was connected are the same: once it is,
  // picking the import up is how they get checked against it.
  const awaitingCrm = data.crmConnected ? progress.awaitingCrm : 0;
  const resumable = !progress.running
    && (progress.cancelled > 0 || progress.failed > 0 || awaitingCrm > 0);

  const toggle = (id: string) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  /**
   * A bulk action over exactly what is selected.
   *
   * The summary comes back per item, so the message names what happened rather than
   * claiming success: "38 approved, 2 changed while you were reading" is the useful
   * sentence.
   */
  const runBulk = async (action: string, extra: { contactType?: string; tagKey?: string } = {}) => {
    setResult(null);
    try {
      // An approval names the plan each row showed; a row that changed since is refused.
      const planHashes = action === 'approve'
        ? Object.fromEntries(items.filter((i) => selected.has(i.id) && i.planHash).map((i) => [i.id, i.planHash!]))
        : undefined;
      const res = await bulk({ itemIds: [...selected], action, ...extra, ...(planHashes ? { planHashes } : {}) }).unwrap();
      const counts = res.results.reduce<Record<string, number>>((acc, r) => {
        acc[r.outcome] = (acc[r.outcome] ?? 0) + 1; return acc;
      }, {});
      setResult(Object.entries(counts)
        .map(([k, v]) => `${v} ${BULK_OUTCOME[k] ?? k.replace(/_/g, ' ')}`).join(' · '));
      setSelected(new Set());
      await refetch();
    } catch {
      setResult('That could not be completed.');
    }
  };

  return (
    <div className="min-w-0 space-y-5">
      {showBack ? (
        <button type="button" onClick={onBack} className="min-h-11 text-sm text-slate-600 underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900">
          ← {backLabel}
        </button>
      ) : null}

      {progress.running ? <ProgressPanel progress={progress} /> : null}
      {!data.crmConnected && progress.awaitingCrm > 0 ? (
        <p className="text-sm text-slate-600" data-awaiting-crm="">
          {`${progress.awaitingCrm.toLocaleString()} contact${progress.awaitingCrm === 1 ? ' is' : 's are'} waiting for a CRM. Connect your CRM in Settings → Connections, then come back here to check ${progress.awaitingCrm === 1 ? 'it' : 'them'} for existing contacts. Nothing is sent to your CRM until you approve it.`}
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        {progress.running && !progress.cancelRequested ? (
          <Button
            variant="secondary" className="min-h-11" disabled={cancelling}
            onClick={() => { void cancelBatch(batchId).then(() => refetch()); }}
          >
            Stop importing
          </Button>
        ) : null}
        {resumable ? (
          <Button
            variant="secondary" className="min-h-11" disabled={resuming}
            onClick={() => { void resumeBatch(batchId).then(() => refetch()); }}
          >
            {/* Says what it will do. "Resume" alone leaves people wondering whether the
                contacts that already worked are about to be redone. */}
            {progress.cancelled + progress.failed === 0
              ? `Check ${awaitingCrm.toLocaleString()} contact${awaitingCrm === 1 ? '' : 's'} against your CRM`
              : 'Pick up where it stopped'}
          </Button>
        ) : null}
        {/*
          A plain link, because the response is a file. Downloading it through fetch
          would mean holding a customer's whole result set in memory to hand it straight
          back to the browser.
        */}
        <a
          href={`/api/intake/batches/${batchId}/export`}
          className="inline-flex min-h-11 items-center rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-800 hover:border-slate-400"
        >
          Download results
        </a>
      </div>

      {/* Where it came from: once, in the drawer's header. */}
      <DrawerSource>
        <p className="text-xs text-slate-500" data-captured-from="">
          {`Captured from ${CAPTURED_FROM[data.batch.source] ?? 'File Import'}`}
          {` · ${data.tally.total} contact${data.tally.total === 1 ? '' : 's'} · ${formatWhen(data.batch.createdAt)}`}
        </p>
      </DrawerSource>

      <div>
        <dl className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
          {[
            ['Ready', n('ready')], ['Needs review', n('needs_review')],
            ['Duplicates', n('duplicate')], ['Not usable', n('invalid')],
            ['Failed', n('failed')], ['Stopped', n('cancelled')],
          ].filter(([label, value]) => value !== 0 || label === 'Ready' || label === 'Needs review').map(([label, value]) => (
            <div key={String(label)} className="rounded-md bg-slate-50 px-3 py-2">
              <dt className="text-xs text-slate-500">{label}</dt>
              <dd className="mt-0.5 text-xl font-semibold text-slate-900">{value}</dd>
            </div>
          ))}
        </dl>
      </div>

      {/*
        Ready means the plan is agreed, not that anything reached the CRM. Saying
        "completed" here would be a claim about a system this product has not written to.
      */}
      {n('ready') > 0 ? (
        <p className="text-sm text-slate-600">
          Ready contacts are checked and waiting for your approval. Nothing is sent to your
          CRM until you approve it.
        </p>
      ) : null}

      {/*
        Select all ELIGIBLE: contacts still to decide that have a plan to approve. A row that
        must be confirmed or matched first (a shared email, an unresolved identity) has no
        plan, so it is never swept into a bulk approval.
      */}
      {eligible.length > 0 ? (
        <label className="inline-flex min-h-11 items-center gap-3 text-sm text-slate-800" data-batch-select-all="">
          <input
            type="checkbox" className="h-5 w-5 rounded border-slate-300"
            checked={eligible.every((i) => selected.has(i.id))}
            onChange={(e) => setSelected(e.target.checked ? new Set(eligible.map((i) => i.id)) : new Set())}
          />
          Select all eligible ({eligible.length})
        </label>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <nav aria-label="Filter contacts" className="-mx-1 flex gap-1 overflow-x-auto px-1">
          {FILTERS.map((f) => {
            const count = f.id === 'all' ? data.tally.total : n(f.id);
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                aria-current={filter === f.id ? 'true' : undefined}
                className={`min-h-11 whitespace-nowrap rounded-full px-3 text-sm ${
                  filter === f.id ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 ring-1 ring-slate-200'
                }`}
              >
                {f.label} <span className="opacity-70">{count}</span>
              </button>
            );
          })}
        </nav>
        <label className="ml-auto min-w-0 flex-1 sm:max-w-xs">
          <span className="sr-only">Search this import</span>
          <input
            type="search" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, company or email…"
            className="min-h-11 w-full rounded-md border border-slate-300 px-3 text-sm"
          />
        </label>
      </div>

      {/*
        The bulk bar appears only with a selection, and says exactly how many. A control
        that could act on "everything matching your filter" without showing the number is
        how somebody approves two thousand contacts by accident.
      */}
      {selected.size > 0 ? (
        <DrawerFooter inlineClassName="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
          <DrawerActions
            summary={`${selected.size} selected`}
            primary={(
              <Button onClick={() => void runBulk('approve')} disabled={acting}>
                Approve {selected.size}
              </Button>
            )}
            secondary={[
              <Button key="none" variant="secondary" onClick={() => void runBulk('reject')} disabled={acting}>
                No action
              </Button>,
              /*
               * One person's Contact Type choice for the selected NEW contacts. Contacts
               * already in the CRM keep the type the CRM records, and are left alone.
               */
              typeList?.supported && typeList.available ? (
                <label key="type" className="block min-w-0">
                  <span className="sr-only">Set Contact Type for the selected new contacts</span>
                  <select
                    value="" disabled={acting}
                    onChange={(e) => { if (e.target.value) void runBulk('set_contact_type', { contactType: e.target.value }); }}
                    className="min-h-11 w-full min-w-0 rounded-md border border-slate-300 bg-white px-3 text-sm"
                  >
                    <option value="">Set Contact Type…</option>
                    {typeList.options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                  </select>
                </label>
              ) : null,
              // One optional CRM tag added to each selected contact; their other tags stay.
              vocab.tags.some((t) => t.kind === 'tag') ? (
                <label key="tag" className="block min-w-0">
                  <span className="sr-only">Add a CRM tag to the selected contacts</span>
                  <select
                    value="" disabled={acting}
                    onChange={(e) => { if (e.target.value) void runBulk('add_tag', { tagKey: e.target.value }); }}
                    className="min-h-11 w-full min-w-0 rounded-md border border-slate-300 bg-white px-3 text-sm"
                  >
                    <option value="">Add tag to selected…</option>
                    {vocab.tags.filter((t) => t.kind === 'tag').map((t) => <option key={t.id} value={t.key}>{t.label}</option>)}
                  </select>
                </label>
              ) : null,
              <Button key="clear" variant="secondary" onClick={() => setSelected(new Set())}>
                Clear selection
              </Button>,
            ]}
          />
        </DrawerFooter>
      ) : null}
      <div aria-live="polite">{result ? <Alert tone="success">{result}</Alert> : null}</div>

      {items.length === 0 ? (
        <EmptyState title="Nothing here">
          No contacts match this filter.
        </EmptyState>
      ) : (
        /* Cards on a phone, a table above sm. A contact row has too many columns to
           squeeze into 390px without something becoming unreadable. */
        <ul className="space-y-2 sm:hidden">
          {items.map((i) => (
            <RowCard
              key={i.id} item={i} tags={tagsOf(i, vocab)}
              selected={selected.has(i.id)}
              onToggle={() => toggle(i.id)}
              onOpen={() => setOpenItem(i.id)}
              openRef={register(i.id)}
            />
          ))}
        </ul>
      )}

      {items.length > 0 ? (
        <Card className="hidden overflow-x-auto sm:block">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2 font-semibold"><span className="sr-only">Select</span></th>
                <th className="px-4 py-2 font-semibold">Row</th>
                <th className="px-4 py-2 font-semibold">Name</th>
                <th className="px-4 py-2 font-semibold">Email</th>
                <th className="px-4 py-2 font-semibold">Company</th>
                <th className="px-4 py-2 font-semibold">CRM tags</th>
                <th className="px-4 py-2 font-semibold">Status</th>
                <th className="px-4 py-2 font-semibold">Proposed</th>
              </tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.id} className="border-b border-slate-100 last:border-0">
                  <td className="px-4 py-2">
                    <label className="flex min-h-11 items-center">
                      <span className="sr-only">Select this contact</span>
                      <input
                        type="checkbox" checked={selected.has(i.id)}
                        onChange={() => toggle(i.id)}
                        className="h-4 w-4 rounded border-slate-300"
                      />
                    </label>
                  </td>
                  <td className="px-4 py-2 text-slate-400">{i.sourceRef?.replace('row:', '') ?? i.position + 1}</td>
                  <td className="max-w-[14rem] truncate px-4 py-2">
                    <button
                      type="button" onClick={() => setOpenItem(i.id)} ref={register(i.id)}
                      className="inline-flex min-h-11 max-w-full items-center truncate text-slate-900 underline decoration-slate-300 hover:decoration-slate-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-slate-900"
                    >
                      {i.candidate?.fullName ?? '—'}
                    </button>
                  </td>
                  <td className="max-w-[18rem] truncate px-4 py-2 text-slate-700">
                    {i.candidate?.emails[0]?.normalized ?? '—'}
                  </td>
                  <td className="max-w-[12rem] truncate px-4 py-2 text-slate-700">
                    {i.candidate?.company ?? '—'}
                  </td>
                  <td className="min-w-[9rem] max-w-[14rem] px-4 py-2 text-slate-700 [overflow-wrap:break-word]">{tagsOf(i, vocab) || 'None'}</td>
                  <td className="px-4 py-2">
                    <span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_TONE[i.status] ?? 'bg-slate-100 text-slate-600'}`}>
                      {statusLabel(i.status)}
                    </span>
                    {i.errorCode ? (
                      <span className="mt-1 block text-xs text-slate-500">
                        {errorLabel(i.errorCode)}
                      </span>
                    ) : null}
                  </td>
                  <td className="px-4 py-2 text-slate-700">{proposedAction(i)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ) : null}
    </div>
  );
}

function RowCard({
  item, tags, selected, onToggle, onOpen, openRef,
}: {
  item: IntakeItem; tags: string; selected: boolean; onToggle: () => void; onOpen: () => void;
  openRef: (el: HTMLButtonElement | null) => void;
}) {
  return (
    <li>
      <Card className="p-4">
        <div className="flex items-start justify-between gap-3">
          <label className="flex min-h-11 items-center pr-1">
            <span className="sr-only">Select this contact</span>
            <input
              type="checkbox" checked={selected} onChange={onToggle}
              className="h-4 w-4 rounded border-slate-300"
            />
          </label>
          <button
            type="button" onClick={onOpen} ref={openRef}
            className="min-w-0 flex-1 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
          >
            <p className="truncate text-sm font-medium text-slate-900">
              {item.candidate?.fullName ?? 'No name'}
            </p>
            <p className="truncate text-xs text-slate-500">
              {item.candidate?.emails[0]?.normalized ?? item.candidate?.phones[0]?.raw ?? '—'}
            </p>
            {item.candidate?.company ? (
              <p className="truncate text-xs text-slate-500">{item.candidate.company}</p>
            ) : null}
          </button>
          <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_TONE[item.status] ?? 'bg-slate-100 text-slate-600'}`}>
            {statusLabel(item.status)}
          </span>
        </div>
        <p className="mt-2 text-xs text-slate-600 [overflow-wrap:anywhere]">
          {proposedAction(item)}
          {tags ? ` · ${tags}` : ''}
        </p>
        {item.errorCode ? (
          <p className="mt-1 text-xs text-slate-500">{errorLabel(item.errorCode)}</p>
        ) : null}
      </Card>
    </li>
  );
}
